import { ControlOutlined, CrownFilled } from "@ant-design/icons";
import { Alert, Button, Card, Drawer, Input, Select, Slider, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import {
    getSurvivorshipDataSetsRequest,
    getSurvivorshipRulesetRequest,
    saveSurvivorshipRulesetRequest,
} from "store/survivorshipRules/actions";

// Survivorship & Trust (PRE-2286, PRE-1170) — authors the schema's SurvivorshipRuleset:
// per-source trust weights, per-field default rules, tie-breakers and the mandatory
// audit rationale. Consumed by the dedup merge engine (weighted-trust merge).

// Schema-level default rules — mirrors the backend SURVIVORSHIP_FIELD_RULES vocabulary
// (NOMINATED_VALUE is cluster-specific and deliberately not offered here).
const RULE_OPTIONS = [
    { value: "MOST_COMMON_VALUE", label: "Most common value" },
    { value: "LARGEST_VALUE", label: "Largest value (numeric)" },
    { value: "SMALLEST_VALUE", label: "Smallest value (numeric)" },
    { value: "LONGEST_STRING", label: "Longest text" },
    { value: "SMALLEST_STRING", label: "Shortest text" },
    { value: "NEWEST_VALUE", label: "Newest value (date/time)" },
    { value: "OLDEST_VALUE", label: "Oldest value (date/time)" },
];

const TIE_BREAKER_OPTIONS = [
    { value: "PRECEDENCE_TIER", label: "Higher precedence tier" },
    { value: "LONGEST", label: "Longer value" },
    { value: "FIRST", label: "Keep survivor's value" },
];

interface ISurvivorshipTrustDrawerProps {
    businessAreaId?: string;
    schemaId?: string;
    schemaName?: string;
    schemaFields?: { fieldId: string; name: string }[];
}

// Datasets that share a trust score (two or more each), by score, highest first. When such sources
// are the most trusted to have a value for a field with no rule of its own and disagree, a merge
// into a golden record, or the creation of one, goes to a steward whatever the tie-breakers say
// (PM decision R2); other merges use the tie-breakers.
export function sharedTrustGroups(dataSets: { dataSetId?: string; dataSetName?: string }[], trustOf: (dataSet: any) => number) {
    const byScore = new Map<number, string[]>();
    for (const dataSet of dataSets) {
        const score = trustOf(dataSet);
        byScore.set(score, [...(byScore.get(score) || []), dataSet.dataSetName || dataSet.dataSetId || ""]);
    }
    return [...byScore.entries()].filter(([, names]) => names.length > 1).sort((a, b) => b[0] - a[0]).map(([score, names]) => ({ score, names }));
}

export default function SurvivorshipTrustDrawer({ businessAreaId, schemaId, schemaName, schemaFields = [] }: ISurvivorshipTrustDrawerProps) {
    const dispatch = useAppDispatch();
    const ruleset = useAppSelector((s) => s.survivorshipRules?.ruleset);
    const getRulesetResult = useAppSelector((s) => s.survivorshipRules?.getRulesetResult);
    const schemaDataSets = useAppSelector((s) => s.survivorshipRules?.schemaDataSets) || [];
    const saving = useAppSelector((s) => s.survivorshipRules?.saving);
    const saveResult = useAppSelector((s) => s.survivorshipRules?.saveRulesetResult);

    // The warning about shared scores applies only where golden records are kept up (a golden
    // dataset in the schema, the tenant's maintenance switch on).
    const goldenMaintenanceOn = useAppSelector((s) => !!(s.userLogin.tenant as { features?: { goldenMaintenance?: boolean } } | undefined)?.features?.goldenMaintenance);
    const [open, setOpen] = React.useState(false);
    const [weights, setWeights] = React.useState<Record<string, number>>({});
    const [fieldRules, setFieldRules] = React.useState<Record<string, string>>({});
    const [tieBreakers, setTieBreakers] = React.useState<string[]>([]);
    const [staticBatchRule, setStaticBatchRule] = React.useState<string | null>(null);
    const [rationale, setRationale] = React.useState("");
    const wasSaving = React.useRef(false);

    const show = () => {
        if (!businessAreaId || !schemaId) {
            return;
        }
        // Reset the editable state BEFORE fetching — otherwise a previously opened schema's
        // ruleset (weights keyed by its dataSetIds, its rules/rationale) bleeds into this
        // schema when it has no saved ruleset (the 404 path hydrates nothing).
        setWeights({});
        setFieldRules({});
        setTieBreakers([]);
        setStaticBatchRule(null);
        setRationale("");
        setOpen(true);
        dispatch(getSurvivorshipRulesetRequest(businessAreaId, schemaId));
        dispatch(getSurvivorshipDataSetsRequest(businessAreaId, schemaId));
    };

    // Hydrate the editable state when the schema's saved ruleset arrives.
    React.useEffect(() => {
        if (open && ruleset && ruleset.schemaId === schemaId) {
            setWeights({ ...(ruleset.sourceTrustWeights || {}) });
            setFieldRules({ ...(ruleset.fieldRules || {}) });
            setTieBreakers([...(ruleset.tieBreakers || [])]);
            setStaticBatchRule(ruleset.staticBatchRule || null);
            setRationale(ruleset.rationale || "");
        }
    }, [open, ruleset, schemaId]);

    // Save outcome feedback (saving true -> false transition).
    React.useEffect(() => {
        if (wasSaving.current && !saving) {
            if (saveResult?.success) {
                message.success("Survivorship ruleset saved.");
                setOpen(false);
            } else {
                message.error(saveResult?.message || "Failed to save the survivorship ruleset.");
            }
        }
        wasSaving.current = !!saving;
    }, [saving, saveResult]);

    // Engine parity (duplicates-service SURVIVORSHIP_TIER_DEFAULT_WEIGHTS): unset sources
    // default by precedence tier — GOLDEN 100, MANUAL_ENTRY 75, API_SYNC 50, PROMOTED_CSV 25,
    // unranked 0. What the sliders display is exactly what Save persists (WYSIWYG).
    const TIER_DEFAULT_WEIGHTS: Record<string, number> = { GOLDEN_NOMINAL: 100, MANUAL_ENTRY: 75, API_SYNC: 50, PROMOTED_CSV: 25 };
    const sliderValue = (dataSet: IDataSet) => {
        if (weights[dataSet.dataSetId as string] !== undefined) {
            return weights[dataSet.dataSetId as string];
        }
        if (dataSet.isGoldenNominal) {
            return 100;
        }
        return TIER_DEFAULT_WEIGHTS[(dataSet as any).precedenceTier as string] ?? 0;
    };

    const hasGoldenDataSet = schemaDataSets.some((dataSet: IDataSet) => dataSet.isGoldenNominal);
    const trustTies = goldenMaintenanceOn && hasGoldenDataSet ? sharedTrustGroups(schemaDataSets, sliderValue) : [];

    const save = () => {
        if (!rationale.trim()) {
            message.warning("A rationale is required for audit (PRE-1170).");
            return;
        }
        // Persist the EFFECTIVE weight of every listed dataset so the saved ruleset matches
        // what the user saw (an unset slider is not silently different from a dragged one).
        const effectiveWeights: Record<string, number> = { ...weights };
        schemaDataSets.forEach((dataSet: IDataSet) => {
            if (dataSet.dataSetId && effectiveWeights[dataSet.dataSetId] === undefined) {
                effectiveWeights[dataSet.dataSetId] = sliderValue(dataSet);
            }
        });
        const payload = {
            sourceTrustWeights: effectiveWeights,
            tieBreakers,
            fieldRules,
            staticBatchRule,
            rationale: rationale.trim(),
        };
        dispatch(saveSurvivorshipRulesetRequest(businessAreaId, schemaId, payload));
    };

    const fieldColumns = [
        {
            title: "Field",
            dataIndex: "name",
            render: (value: string) => <Typography.Text strong>{value}</Typography.Text>,
        },
        {
            title: "Default survivorship rule",
            dataIndex: "fieldId",
            width: 260,
            render: (fieldId: string) => (
                <Select
                    className="w-full"
                    allowClear
                    placeholder="Weighted trust (default)"
                    value={fieldRules[fieldId]}
                    options={RULE_OPTIONS}
                    onChange={(value) =>
                        setFieldRules((prev) => {
                            const next = { ...prev };
                            if (value) {
                                next[fieldId] = value;
                            } else {
                                delete next[fieldId];
                            }
                            return next;
                        })
                    }
                />
            ),
        },
    ];

    return (
        <>
            <Tooltip title="Configure survivorship rules and source trust for this schema">
                <Button icon={<ControlOutlined />} onClick={show} data-testid="survivorship-trust-button">
                    Survivorship &amp; Trust
                </Button>
            </Tooltip>
            <Drawer
                title={
                    <span className="inline-flex items-center gap-2">
                        <CrownFilled className="text-amber-500" />
                        Survivorship &amp; Trust — {schemaName}
                    </span>
                }
                width={680}
                open={open}
                onClose={() => setOpen(false)}
                data-testid="survivorship-trust-drawer"
                extra={
                    <Button type="primary" loading={!!saving} disabled={getRulesetResult ? !getRulesetResult.success : false} onClick={save} data-testid="survivorship-save">
                        Save ruleset
                    </Button>
                }
            >
                <Space direction="vertical" className="w-full" size="middle">
                    {getRulesetResult && !getRulesetResult.success && (
                        <Alert
                            type="error"
                            showIcon
                            message="Could not load this schema's saved ruleset"
                            description="Saving is disabled to avoid overwriting an existing configuration. Close and retry."
                            data-testid="survivorship-load-error"
                        />
                    )}
                    <Alert
                        type="info"
                        showIcon
                        message="How merges use this"
                        description="During deduplication, each field of the surviving record is filled from the most trusted non-empty source. A user-picked merge rule on a cluster always wins; otherwise the per-field default below applies; otherwise the highest-trust source value is taken."
                    />
                    <Card size="small" title="Source trust scores (0–100)">
                        {schemaDataSets.length === 0 ? (
                            <Typography.Text type="secondary">No datasets in this schema yet.</Typography.Text>
                        ) : (
                            schemaDataSets.map((dataSet: IDataSet) => (
                                <div key={dataSet.dataSetId} className="mb-2 flex items-center gap-3" data-testid="survivorship-trust-row">
                                    <span className="w-44 shrink-0 truncate" title={dataSet.dataSetName}>
                                        {dataSet.dataSetName}
                                        {dataSet.isGoldenNominal && (
                                            <Tag color="gold" className="ml-2">
                                                GOLDEN
                                            </Tag>
                                        )}
                                    </span>
                                    <Slider
                                        className="flex-1"
                                        min={0}
                                        max={100}
                                        value={sliderValue(dataSet)}
                                        onChange={(value: number) => setWeights((prev) => ({ ...prev, [dataSet.dataSetId as string]: value }))}
                                    />
                                    <span className="w-10 text-right">{sliderValue(dataSet)}</span>
                                </div>
                            ))
                        )}
                    </Card>
                    {trustTies.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            data-testid="survivorship-trust-tie-warning"
                            title="Some sources share a trust score"
                            description={
                                <>
                                    {trustTies.map((group) => (
                                        <div key={group.score}>
                                            {group.names.join(", ")}: {group.score}
                                        </div>
                                    ))}
                                    When these are the most trusted sources with a value for a field that has no rule of its own, and they disagree,
                                    merging into a golden record, or creating one, waits for a steward, whatever the tie-breakers below. Give them
                                    different scores, or the field a rule, to have it settled automatically. Other merges use the tie-breakers.
                                </>
                            }
                        />
                    )}
                    <Card size="small" title="Per-field default rules">
                        <Table rowKey="fieldId" size="small" columns={fieldColumns} dataSource={schemaFields} pagination={false} />
                    </Card>
                    <Card size="small" title="Batch default & tie-breakers">
                        <div className="mb-2 flex items-center gap-3">
                            <span className="w-44 shrink-0">Static batch rule</span>
                            <Select
                                className="flex-1"
                                allowClear
                                placeholder="None — weighted trust decides"
                                value={staticBatchRule || undefined}
                                options={RULE_OPTIONS}
                                onChange={(value) => setStaticBatchRule(value || null)}
                            />
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-44 shrink-0">Tie-breakers (in order)</span>
                            <Select
                                className="flex-1"
                                mode="multiple"
                                placeholder="Keep survivor's value"
                                value={tieBreakers}
                                options={TIE_BREAKER_OPTIONS}
                                onChange={(value) => setTieBreakers(value)}
                            />
                        </div>
                    </Card>
                    <Card size="small" title="Rationale (required — documented for audit)">
                        <Input.TextArea
                            rows={3}
                            value={rationale}
                            placeholder="Explain why these survivorship rules were chosen…"
                            onChange={(event) => setRationale(event.target.value)}
                            data-testid="survivorship-rationale"
                        />
                    </Card>
                </Space>
            </Drawer>
        </>
    );
}
